import DeleteIcon from "@assets/icons/delete.svg";
import DuplicateIcon from "@assets/icons/duplicate.svg";
import StopIcon from "@assets/icons/hand-stop.svg";
import InfoIcon from "@assets/icons/info.svg";
import PlayIcon from "@assets/icons/play.svg";
import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useOnceEffect } from "@core/hooks/use-once-effect";
import { useI18n } from "@core/i18n/i18n";
import { useNavigate } from "@core/routing/use-navigate";
import { onboardingManager, programManager, templateOperationsManager } from "@core/service-container";
import { AnalyticsCategory } from "@domain/analytics/analytics-types";
import { useFormatPatients } from "@domain/patient/hooks/use-patients";
import { Patient, PendingPatient } from "@domain/patient/patient";
import {
	isNewProgram,
	isProgram,
	NewProgram,
	Program,
	ProgramContent,
	programContentAreEquals,
	programContentDeepCopy,
} from "@domain/program/program";
import {
	AnalyticsProgramLocation,
	sendAnalyticsEventDeleteProgram,
	sendAnalyticsEventEditProgram,
	sendAnalyticsEventSaveProgram,
	sendAnalyticsEventStopProgram,
} from "@domain/program/program-analytics";
import { ProgramContentEditor } from "@domain/program/program-content-editor";
import { ProgramStatus } from "@domain/program/program-status/program-status";
import { Template } from "@domain/template/template";
import { useUser } from "@domain/user/hooks/use-user";
import { WordingKey } from "@typings/i18n-typings";
import { Banner } from "@ui/components/common/banner";
import { DialogContext, ModalId } from "@ui/components/dialog/dialog-provider";
import { ToastContext } from "@ui/components/common/toast-provider";
import { TrackingContextProvider } from "@ui/components/common/tracking-context-provider";
import { RoutesPath, TabPatientDetails } from "@ui/navigation/routes";
import { ScheduleProgramFormData } from "@ui/pages/program-builder/dialog/schedule-dialog/schedule-program-dialog";
import { useProgramActions } from "@ui/pages/program-builder/hooks/use-program-actions";
import {
	ProgramContentEditorView,
	ProgramContentEditorViewProps,
} from "@ui/pages/program-builder/program-content-editor-view";
import { isNotNil } from "@utils/assert";
import { useComputedObservable, useObservable } from "micro-observables";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";

const PrimaryButtonLabel = {
	[ProgramStatus.Ongoing]: "programBuilder.editOngoingProgramButton.v2_1",
	[ProgramStatus.Scheduled]: "programBuilder.editScheduleProgramButton.v2_1",
	[ProgramStatus.Draft]: "programBuilder.startButton",
	[ProgramStatus.Ended]: "programBuilder.editEndedProgramButton.v2_1",
} satisfies Record<ProgramStatus, WordingKey>;

export const ProgramBuilderView: React.FC<{
	patient: Patient | PendingPatient;
	program: Program | NewProgram;
	template?: Template;
	programIsLoading: boolean;
	openScheduleModal?: boolean;
}> = ({ patient, program, template, programIsLoading, openScheduleModal }) => {
	const [isUpdating, setIsUpdating] = useState(false);
	const { format } = useI18n();
	const user = useUser();
	const { formatPatientName } = useFormatPatients();
	const defaultProgramContent = useMemo(
		() => ({
			name: program.name ?? format("programBuilder.defaultTitle.v2_1", { patientName: patient.firstName }),
			days: program.days,
			startDate: program.startDate,
			endDate: program.endDate,
		}),
		[format, patient.firstName, program.days, program.endDate, program.name, program.startDate],
	);

	const programContentFromTemplate = useMemo(
		() => (template ? { name: defaultProgramContent.name, days: template.days } : undefined),
		[defaultProgramContent.name, template],
	);

	const isViewOnly = program.status === ProgramStatus.Ended;

	const { duplicateAsDraftProgram, duplicateAsTemplateProgram, loading } = useProgramActions();

	// if viewOnly change it should not reset the content so there is a useEffect to change editable property if viewOnly change
	// same for dataIsLoading
	const programEditor = useMemo(
		() =>
			new ProgramContentEditor(
				programContentDeepCopy(defaultProgramContent),
				onboardingManager,
				programIsLoading,
			),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	useEffect(() => {
		programEditor.editProgram(programContentDeepCopy(defaultProgramContent));
	}, [defaultProgramContent, programEditor]);

	const { showToast } = useContext(ToastContext);
	const dialogContext = useContext(DialogContext);
	const patientId = patient.id;

	const navigate = useNavigate();
	const [isApplyingRemoteChanges, setIsApplyingRemoteChanges] = useState(false);
	const [hasPendingChanges, setHasPendingChanges] = useState(false);
	const [editedProgramIsEmpty, setEditedProgramIsEmpty] = useState(defaultProgramContent.days.isEmpty);
	const editedProgram = useObservable(programEditor.editedProgram);

	const isProgramFromTemplate = useComputedObservable(
		() => templateOperationsManager.lastTemplateOperation.get()?.type === "assign",
		[],
	);

	const navigateToNewProgram = useCallback(
		(programId: string) =>
			navigate(RoutesPath.ProgramBuilder, { route: { patientId }, query: { programId } }, { replace: true }),
		[navigate, patientId],
	);

	const [saveProgram, loadingSave] = useAsyncOperation(
		async (editedContent: ProgramContent) => {
			try {
				setIsApplyingRemoteChanges(true);
				const editedContentWithDefaultName = {
					days: editedContent.days,
					name: editedContent.name,
				};
				let metadata;
				if (isNewProgram(program)) {
					metadata = await programManager.createDraftProgram(patientId, editedContentWithDefaultName, () => false);
					sendAnalyticsEventSaveProgram(patient, metadata.id);
				} else {
					await programManager.saveProgram(patientId, program.id, editedContentWithDefaultName);
					if (program.status === ProgramStatus.Draft) {
						sendAnalyticsEventSaveProgram(patient, program.id);
					} else {
						sendAnalyticsEventEditProgram();
					}
				}
				showToast(
					format("programBuilder.saveButton.successToast.title"),
					format("programBuilder.saveButton.successToast.message"),
					"success",
				);
				if (isNewProgram(program) && metadata) {
					navigateToNewProgram(metadata.id);
				}
				if (!isNewProgram(program) && template?.id) {
					navigateToNewProgram(program.id);
				}
				setIsApplyingRemoteChanges(false);
			} catch (error) {
				console.error("Failed to save program", error);
				showToast(
					format("programBuilder.saveButton.errorToast.title"),
					format("programBuilder.saveButton.errorToast.message"),
				);
				setIsApplyingRemoteChanges(false);
			}
		},
		{
			deps: [format, navigateToNewProgram, patient.analyticsId, patientId, program, showToast, template?.id],
		},
	);

	const getStatusWording = useCallback(() => {
		switch (program.status) {
			case ProgramStatus.Ongoing:
				return format("programBuilder.programStatus.ongoing.v2_1");
			case ProgramStatus.Scheduled:
				return format("programBuilder.programStatus.scheduled.v2_1");
			case ProgramStatus.Draft:
				return format("programBuilder.programStatus.draft");
			case ProgramStatus.Ended:
				return format("programBuilder.programStatus.ended.v2_1");
		}
	}, [format, program.status]);

	const navigateBackToPatientPrograms = useCallback(() => {
		setIsApplyingRemoteChanges(true);
		setTimeout(() => {
			navigate(
				RoutesPath.PatientDetails,
				{
					route: { patientId: patient.id },
					query: { active: TabPatientDetails.PROGRAMS_TAB },
				},
				{ replace: true },
			);
			dialogContext?.closeAll();
			setIsApplyingRemoteChanges(false);
		}, 100);
	}, [dialogContext, navigate, patient.id]);

	const confirmDuplication = useCallback(
		(onConfirm: () => Promise<void>) => {
			dialogContext?.openModal(ModalId.ConfirmDuplication, { onConfirm });
		},
		[dialogContext],
	);

	const availableActions = useMemo(() => {
		if (isNewProgram(program)) {
			return [];
		}

		const runDuplicateAsDraft = async () =>
			duplicateAsDraftProgram({
				patientId: patient.id,
				programName: program.name,
				programId: program.id,
			});

		const handleDuplicateAsDraft = async () => {
			if (hasPendingChanges) {
				confirmDuplication(runDuplicateAsDraft);
				return;
			}
			await runDuplicateAsDraft();
		};

		const runDuplicateAsTemplate = async (templateName: string) =>
			duplicateAsTemplateProgram({
				patientId: patient.id,
				programId: program.id,
				templateName,
			});

		const handleDuplicateAsTemplateSubmit = async (templateName: string) => {
			if (hasPendingChanges) {
				confirmDuplication(() => runDuplicateAsTemplate(templateName));
				return;
			}
			await runDuplicateAsTemplate(templateName);
			dialogContext?.closeAll();
		};

		const deleteAction = {
			text: format("programBuilder.popupMenu.deleteProgram"),
			icon: <DeleteIcon height={24} width={24} />,
			destructive: true,
			onClick: () => {
				dialogContext?.openModal(ModalId.DeleteProgram, {
					patient,
					program,
					onSuccess: () => {
						navigateBackToPatientPrograms();
						sendAnalyticsEventDeleteProgram(patient, program.id, AnalyticsProgramLocation.PROGRAM_BUILDER);
					},
				});
			},
		};

		const duplicateAsDraftAction = {
			text: format("global.programOperation.duplicateAsDraft"),
			icon: <DuplicateIcon height={24} width={24} />,
			onClick: handleDuplicateAsDraft,
		};

		const duplicateAsTemplateAction = {
			text: format("global.programOperation.duplicateAsTemplate"),
			icon: <DuplicateIcon height={24} width={24} />,
			onClick: () => {
				dialogContext?.openModal(ModalId.DuplicateAsTemplateProgram, {
					programName: editedProgram.name,
					onSubmit: handleDuplicateAsTemplateSubmit,
				});
			},
		};

		const stopAction = {
			text: format("programBuilder.popupMenu.stopProgram"),
			icon: <StopIcon height={24} width={24} />,
			destructive: true,
			onClick: () => {
				isNotNil(user) &&
					dialogContext?.openModal(ModalId.StopProgram, {
						patient,
						program,
						editor: user,
						onSuccess: () => {
							navigateBackToPatientPrograms();
							sendAnalyticsEventStopProgram(patient, program.id, AnalyticsProgramLocation.PROGRAM_BUILDER);
						},
					});
			},
		};

		switch (program.status) {
			case ProgramStatus.Scheduled:
			case ProgramStatus.Draft:
				return [duplicateAsDraftAction, duplicateAsTemplateAction, deleteAction];
			case ProgramStatus.Ongoing:
				return [duplicateAsDraftAction, duplicateAsTemplateAction, stopAction];
			case ProgramStatus.Ended:
				return [duplicateAsTemplateAction, deleteAction];
		}
	}, [
		dialogContext,
		editedProgram.name,
		format,
		confirmDuplication,
		duplicateAsDraftProgram,
		duplicateAsTemplateProgram,
		navigateBackToPatientPrograms,
		hasPendingChanges,
		patient,
		program,
		user,
	]);
	useEffect(() => () => templateOperationsManager.removeLastTemplateOperation(), []);

	const secondaryButtonProps: ProgramContentEditorViewProps["secondaryButton"] = useMemo(() => {
		if (program.status === ProgramStatus.Draft) {
			return {
				label: format("programBuilder.saveButton"),
				disabled: editedProgramIsEmpty || !hasPendingChanges,
				disabledTooltipMessage: editedProgramIsEmpty
					? format("programBuilder.cantSaveProgramTooltipMessage")
					: format("programBuilder.cantSaveBecauseAlreadySavedTooltipMessage"),
				onClick: saveProgram,
				loading: loadingSave,
			};
		}
	}, [editedProgramIsEmpty, format, hasPendingChanges, loadingSave, program.status, saveProgram]);

	const openStartProgramDialog = useCallback(
		(editedContent: ProgramContent) => {
			if (user) {
				dialogContext?.openModal(ModalId.StartProgram, {
					patient,
					editor: user,
					program,
					editedProgram: editedContent,
					onSuccess: async () => {
						if (program.status === ProgramStatus.Draft) {
							navigateBackToPatientPrograms();
						}
					},
					skipDialogConfirmation:
						program.status === ProgramStatus.Ongoing || program.status === ProgramStatus.Scheduled,
					handleLoading: setIsUpdating,
				});
			}
		},
		[dialogContext, navigateBackToPatientPrograms, patient, program, user],
	);

	const primaryButtonProps: ProgramContentEditorViewProps["primaryButton"] = useMemo(
		() => {
			let primaryButtonIcon: React.ReactNode | undefined;
			if (program.status === ProgramStatus.Ongoing) {
				primaryButtonIcon = "pi pi-save";
			} else if (program.status === ProgramStatus.Draft) {
				primaryButtonIcon = <PlayIcon className="p-button-icon-right" width={16} height={16} />;
			}

			return {
				disabled: editedProgramIsEmpty,
				label: format(PrimaryButtonLabel[program.status]),
				icon: primaryButtonIcon,
				disabledTooltipMessage: format("programBuilder.cantStartProgramTooltipMessage"),
				onClick: async editedContent => {
					if (program.status === ProgramStatus.Ended) {
						await duplicateAsDraftProgram({
							patientId: patient.id,
							programName: program.name,
							programId: program.id,
							onSuccess: navigateBackToPatientPrograms,
						});
						return;
					}
					if (isNotNil(user)) {
						openStartProgramDialog(editedContent);
					}
				},
				loading: loading || isUpdating,
			};
		},
		[
			editedProgramIsEmpty,
			format,
			program,
			loading,
			isUpdating,
			user,
			openStartProgramDialog,
			duplicateAsDraftProgram,
			patient.id,
			navigateBackToPatientPrograms,
		],
	);

	const openDatesScheduler = useCallback(
		(editedContent: ProgramContent) => {
			dialogContext?.openModal(ModalId.ScheduleProgram, {
				patient,
				editedProgram: editedContent,
				program,
				onSave: (dates: ScheduleProgramFormData) => programEditor.editProgramDates(dates.startDate, dates.endDate),
				secondaryButtonProps:
					program.status === ProgramStatus.Ongoing
						? {
								label: format("programBuilder.scheduleDialog.updateProgramButton"),
								onClick: (dates: ScheduleProgramFormData) => {
									programEditor.editProgramDates(dates.startDate, dates.endDate);
									openStartProgramDialog({ ...editedContent, ...dates });
								},
								loading: isUpdating,
							}
						: undefined,
			});
		},
		[dialogContext, format, isUpdating, openStartProgramDialog, patient, program, programEditor],
	);

	useOnceEffect(() => openDatesScheduler(editedProgram), openScheduleModal, [openScheduleModal]);

	return (
		<TrackingContextProvider category={AnalyticsCategory.PROGRAM} programId={isProgram(program) ? program.id : ""}>
			{isProgramFromTemplate && (
				<Banner
					uiStyle="success"
					icon={InfoIcon}
					text={format("programBuilder.assignTemplateBanner.v1_6", { name: formatPatientName(patient) })}
					onDelete={() => templateOperationsManager.removeLastTemplateOperation()}
				/>
			)}

			<ProgramContentEditorView
				patient={patient}
				programContent={defaultProgramContent}
				programEditor={programEditor}
				programStatus={program.status}
				initialNotSavedProgramContent={programContentFromTemplate}
				statusMessage={getStatusWording()}
				availableActions={availableActions}
				onProgramContentChanged={newProgramContent => {
					setHasPendingChanges(!programContentAreEquals(newProgramContent, defaultProgramContent));
					setEditedProgramIsEmpty(newProgramContent.days.isEmpty);
				}}
				secondaryButton={secondaryButtonProps}
				primaryButton={primaryButtonProps}
				dataIsLoading={programIsLoading}
				quitBlockingEnabled={!isApplyingRemoteChanges}
				viewOnly={isViewOnly}
				disabledLibraryTooltip={format("programBuilder.cantOpenFormerExerciseLibraryTooltipMessage")}
				onProgramSchedulerClick={openDatesScheduler}
			/>
		</TrackingContextProvider>
	);
};
